// Behavior-level content. No internal implementation (fetch logic, caches,
// display caps, known-domain lists) — only what the user sees and does.

export default {
  title: 'Sellers.json Checker',
  summary:
    "Audit any ad system's sellers.json — see who they're authorized to sell for, look up a specific seller, and cross-check ads.txt relationships against it.",

  sections: [
    {
      h: 'What this tool does',
      p: [
        "Pulls a given ad system's published `sellers.json` and breaks down what's inside: how many sellers it lists, the split of publishers vs intermediaries, how many are confidential, and which entries have problems — missing IDs, missing or invalid seller types, or non-confidential sellers with no domain.",
        "It also lets you look up a single seller across the whole file by ID, name, or domain, and cross-check a set of ads.txt records against the file to confirm each declared relationship actually matches how the seller classifies that account.",
      ],
    },
    {
      h: 'When to use it',
      list: [
        'Vetting a new SSP or reseller — you want to see how transparent their sellers.json is before you trust the path.',
        'A seller ID turned up in a chain or an ads.txt line and you need to know who it actually belongs to.',
        "You have ads.txt entries and want to confirm the account types line up with what the SSP declares.",
      ],
    },
    {
      h: 'How to use it',
      steps: [
        '**Audit a whole file.** Enter the ad system\u2019s domain. You get a summary of seller types, the confidential count, and a list of formatting issues — each pointing to the specific seller it came from.',
        '**Look up one seller.** Give the domain plus a seller ID (or a name / domain fragment). The search runs across the entire file and returns the best match plus any others.',
        '**Cross-check ads.txt.** Feed in a set of ads.txt records and the tool reports, per line, whether the seller ID exists and whether the declared relationship (`DIRECT` / `RESELLER`) matches the sellers.json seller type — flagging mismatches.',
      ],
    },
    {
      callout:
        'A **confidential** seller is allowed to hide its name and domain — that\u2019s permitted by the spec, not missing data. A missing domain is only flagged on non-confidential sellers.',
    },
  ],

  faqs: [
    {
      q: "The seller ID isn't found, but I know they sell this inventory.",
      a: 'Seller IDs are matched exactly. If the SSP lists the account under a different ID — common when a publisher holds multiple seats — search by name or domain instead.',
    },
    {
      q: 'What does a type mismatch actually mean?',
      a: "Your ads.txt line claims a relationship the SSP's own sellers.json doesn't back up — e.g. you list them as DIRECT but they classify the account as an intermediary. That's exactly the gap a buyer's supply-path check will catch.",
    },
    {
      q: "Some SSPs won't load.",
      a: "A few publish their sellers.json at a non-standard location or behind protections that block automated retrieval. If the file can't be fetched, that's a retrieval failure — not an empty or invalid sellers.json.",
    },
  ],
};
